import api from "./api";

/**
 * Servicios para la carga de nómina de inhabilitados.
 *
 * - Los tipos de inhabilitación se leen de `exclusion-tipo` (traen `exclusionLevel`,
 *   que decide si se debe pedir un cliente).
 * - Los clientes se leen de `cliente`.
 * - La carga envía las filas ya parseadas y asignadas a `inhabilitados/carga`.
 */

export interface ExclusionType {
  excTipoId: string;
  excTipoNombre: string;
  excTipoDescripcion?: string;
  /** "all" | "auto" | "busqueda" | "cliente" */
  exclusionLevel: string;
}

export interface ClientOption {
  clienteId: string;
  clienteNombre: string;
  clienteDni?: string;
}

export interface InhabilitadoCargaRegistro {
  rut: string;
  nombres?: string;
  apellidos?: string;
  excTipoId: string;
  /** Uno o varios clientes cuando el tipo es de nivel cliente. */
  clienteIds?: string[];
  comentario: string;
}

export interface InhabilitadoCargaPayload {
  fileName: string;
  sheetName: string;
  registros: InhabilitadoCargaRegistro[];
}

export interface InhabilitadoCargaResumen {
  totalRegistros: number;
  creados: number;
  personasCreadas: number;
  personasVinculadas: number;
  duplicados: number;
  rutInvalidos: number;
  tipoInvalidos: number;
  clienteRequerido: number;
  clienteInvalidos: number;
  comentarioRequerido: number;
  errores: number;
}

export interface InhabilitadoCargaDetalle {
  fila: number;
  rut: string;
  rutNormalizado: string;
  nombre: string;
  excTipoId: string;
  tipo: string;
  nivel: string;
  clienteId: string | null;
  cliente: string;
  comentario: string;
  personaId: string | null;
  personaCreada: boolean;
  excPerId: string | null;
  estado: string;
  mensaje: string;
}

export interface InhabilitadoCargaResult {
  message: string;
  resumen: InhabilitadoCargaResumen;
  detalle: InhabilitadoCargaDetalle[];
}

const getUserId = (): string => sessionStorage.getItem("usuarioId") ?? "";

/** Lista de tipos de inhabilitación con su nivel (cliente/all/auto/busqueda). */
export const fetchExclusionTypes = async (): Promise<ExclusionType[]> => {
  const result = await api.get("exclusion-tipo");
  const data = result?.data?.data;
  return Array.isArray(data) ? data : [];
};

/** Lista de clientes para inhabilitaciones a nivel cliente. */
export const fetchClients = async (): Promise<ClientOption[]> => {
  const userId = getUserId();
  const result = await api.get("cliente", { params: { userId } });
  const data = result?.data?.data;
  return Array.isArray(data) ? data : [];
};

/** Envía la nómina ya parseada y asignada; devuelve resumen + detalle por fila. */
export const processInhabilitadosNomina = async (
  payload: InhabilitadoCargaPayload
): Promise<InhabilitadoCargaResult> => {
  const userId = getUserId();
  if (!userId) {
    throw new Error("No se pudo identificar al usuario de la sesión.");
  }
  const result = await api.post("inhabilitados/carga", payload, {
    params: { userId },
  });
  return result.data as InhabilitadoCargaResult;
};
